import React, { memo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { Field, useFormState } from 'react-final-form';
import { setCurrentCityIsDeferred, setError } from '../../../actions';
import { cityQuery } from '../../../queries';
import NewSearchPopupLocations from './NewSearchPopupLocations';
import NewSearchLocation from './NewSearchLocation';
import useLocationsPlaceholder from '../../../hooks/useLocationsPlaceholder';
import Loader from '../../Loader';
import SelectLocation from '../../ItemCreationForm/SelectLocation';
import { FORM_VALUES } from '../../../utils/helpers/constants';
import { regionsQuery } from '../../../queries/regions';
import ErrorField from '../../ItemCreationForm/AddNewItemForm/components/ErrorField';
import { useUndoFilters } from '../../../hooks/useUndoFilters';

const listTypesMap = {
  search: NewSearchLocation,
  create: SelectLocation
};

const NewSearchLocations = ({
  listType = 'search',
  formValues,
  modalParams,
  isInitialScreen,
  setInitCoordinates,
  toggleHandlerCities = () => {},
  locationsPopupIsOpen,
  setLocationsIsLoading,
  locationsPopupWillOpen = false,
  toggleHandlerLocations
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const undoFilters = useUndoFilters();
  const { values } = useFormState();
  const storedCity = useSelector(state => state.searchForm.currentCity);
  const chosenLocationNamesSearch = useSelector(
    state => state.searchForm.chosenLocationNames
  );
  const isCreate = listType === 'create';
  const createValue = values[FORM_VALUES.locationName]
    ? [values[FORM_VALUES.locationName]]
    : [];
  const chosenLocationNames = isCreate
    ? createValue
    : chosenLocationNamesSearch;
  const cityId = formValues.city_id || storedCity;
  const { locationsPlaceholder } = useLocationsPlaceholder(cityId);
  const locationsQueryVariables = { cityId };
  const { data: res } = useQuery(regionsQuery(locationsQueryVariables), {
    variables: locationsQueryVariables
  });
  const hasLocations = !!res?.regions?.length;
  const placeholder =
    isCreate && locationsPlaceholder
      ? t('new_ad.district')
      : locationsPlaceholder || '';
  const ListComponent = listTypesMap[listType];

  const { data, loading, error } = useQuery(
    cityQuery({
      id: cityId
    }),
    {
      variables: {
        id: cityId
      }
    }
  );

  useEffect(() => {
    if (setLocationsIsLoading) {
      setLocationsIsLoading(loading);
    }
  }, [loading]);

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  const cityName =
    data && data.city !== null ? data.city.name : t('search.city_search.any');

  const popupTopTools = () => {
    return (
      <div
        style={{ marginTop: hasLocations ? 0 : '60px' }}
        className="new-search-popup__top-tools-container"
      >
        <div className="new-search-popup__top-tools">
          <span
            tabIndex="0"
            role="button"
            data-cy="city-container"
            onClick={toggleHandlerCities}
            onKeyPress={toggleHandlerCities}
            className="new-search-form__city"
          >
            <div className="new-search-form__city_label" data-cy="city-label">
              {t('new_search.your_city')}
            </div>
            <Loader
              loading={loading}
              optionalClass="new-search-popup__top-tools_loader"
            >
              <div className="new-search-form__city_name">{cityName}</div>
            </Loader>
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    dispatch(setCurrentCityIsDeferred(locationsPopupWillOpen));
  }, [locationsPopupWillOpen]);

  const handleUndo = useCallback(() => {
    if (!isInitialScreen) {
      return;
    }
    undoFilters();
  }, []);

  return (
    <div>
      <Field name="location_ids" component="input" type="hidden" />
      {hasLocations && (
        <div
          role="button"
          tabIndex="0"
          data-cy="chosen-locations"
          onClick={toggleHandlerLocations}
          id="new-search-form-locations-row"
          onKeyPress={toggleHandlerLocations}
          data-stat="new-search-form-locations"
          className={`new-search__locations_row ${
            isCreate ? 'rotate-icon' : ''
          }`}
        >
          {chosenLocationNames?.length ? (
            <div className="new-search__locations_label">
              <div className="city__label">{placeholder}</div>
              <div
                className="new-search__locations_names"
                data-cy="chosen-locations-names"
              >
                {chosenLocationNames.join(', ')}
              </div>
            </div>
          ) : (
            <div
              className="new-search__locations_placeholder"
              data-cy="chosen-locations-placeholder"
            >
              {placeholder}
            </div>
          )}
        </div>
      )}
      {hasLocations && (
        <ErrorField
          field={FORM_VALUES.locationId}
          margin={chosenLocationNames?.length === 1 || !isCreate ? 0 : '15px'}
          cypress="location-id-field-error"
        />
      )}
      <NewSearchPopupLocations
        undo={handleUndo}
        listType={listType}
        modalId="locations"
        isOpen={locationsPopupIsOpen}
        data-cy="modal-select-locations"
        closeHandler={toggleHandlerLocations}
        additionalClass="new-search-popup--locations"
        topTools={!isCreate ? popupTopTools(formValues) : undefined}
        inputPlaceholder={isCreate ? t('new_ad.find_district') : placeholder}
        popupTitle={
          isCreate ? t('new_ad.district') : t('search.locations_search.title')
        }
      >
        {(
          searchString,
          isFocused,
          cancelHandler,
          isSubmittedForm,
          setRef,
          isInputOnFocus
        ) => {
          return (
            <ListComponent
              noLocations={!hasLocations}
              toggleHandlerCities={toggleHandlerCities}
              closePopupHandler={toggleHandlerLocations}
              {...{
                isFocused,
                modalParams,
                searchString,
                cancelHandler,
                isInputOnFocus,
                setInitCoordinates
              }}
            />
          );
        }}
      </NewSearchPopupLocations>
    </div>
  );
};

NewSearchLocations.propTypes = {
  listType: PropTypes.string,
  modalParams: PropTypes.object,
  isInitialScreen: PropTypes.bool,
  setInitCoordinates: PropTypes.func,
  toggleHandlerCities: PropTypes.func,
  setLocationsIsLoading: PropTypes.func,
  locationsPopupWillOpen: PropTypes.bool,
  formValues: PropTypes.object.isRequired,
  locationsPopupIsOpen: PropTypes.bool.isRequired,
  toggleHandlerLocations: PropTypes.func.isRequired
};

export default memo(NewSearchLocations);
